/**
 * TradingSocketService
 *
 * Main thread service that communicates with the TradingSocketWorker.
 * Provides a clean API for widgets to interact with the trading socket.
 */
import type { TradingSocketService, BatchOrderInput } from '.';
import type { TradingSocketProvider } from './TradingSocketProvider';
import type {
    TradingSocketClientContext,
    TradingSocketWorkerRequest,
    TradingSocketWorkerResponse,
    TradingSocketWorkerEvent,
    TradingSocketWorkerMessage,
    TradingSocketWorkerState,
} from './types';
import { effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { reviver } from '@thinkalpha/common/serialization/serializer.js';
import type {
    Order,
    OrderUpdate,
    OrderUpdateReport,
    NewOrderBase,
    NewOrderBatch,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import { inject, injectable, optional } from 'inversify';
import { Subject } from 'rxjs';
import { getDatadogSessionId } from 'src/lib/datadogSession';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import type { SendOrderData } from 'src/routes/widgets/TradeProWidget/api';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

/** Serializable snapshot of the worker's state for Redux DevTools */
interface TradingSocketDevtoolsState {
    workerState: TradingSocketWorkerState;
    isConnected: boolean;
    isAuthenticated: boolean;
    joinedAccountCount: number;
}

export class TradingSocketError extends Error {
    requestId: string;

    constructor(message: string, requestId: string) {
        super(message);
        this.name = 'TradingSocketError';
        this.requestId = requestId;
    }
}

class TradingSocketTokenRejectedError extends TradingSocketError {}

@injectable()
export class TradingSocketServiceImpl implements TradingSocketService {
    #disposableStack = new DisposableStack();
    #disposed = false;
    #log: ReactBindings['Logger'];
    #port!: MessagePort;
    /** Stable identity for this service instance (one worker port), included in worker-side logs. */
    readonly #clientId = v4();
    #pendingRequests = new Map<
        string,
        {
            resolve: (value: TradingSocketWorkerResponse) => void;
            reject: (reason: Error) => void;
            timeoutId: ReturnType<typeof setTimeout>;
        }
    >();
    // Request timeout in milliseconds (30 seconds)
    readonly #REQUEST_TIMEOUT_MS = 30_000;

    // Connection state signals
    #workerState = signal<TradingSocketWorkerState>('initial');
    #isConnected = signal<boolean>(false);
    #isAuthenticated = signal<boolean>(false);

    // Observables for order events
    #orderUpdates$ = new Subject<OrderUpdateReport & OrderUpdate>();
    #orderSnapshots$ = new Subject<Order>();

    // Tracks which accounts are currently joined in the worker
    #joinedAccounts = signal<Set<string>>(new Set());

    // Current accounts this instance is interested in
    #currentAccounts = new Set<string>();

    // Prevents concurrent reconciliation
    #reconcilingAccounts = false;
    // Tracks if another reconciliation was requested while one is running
    #reconcileQueued = false;

    // Track in-progress operations to prevent concurrent calls
    #authenticatePromises = new Map<string, Promise<void>>();
    #effectiveAccessToken: string | null | undefined;
    #lastRejectedAccessToken: string | null = null;
    #initPromise: Promise<void>;

    // DevTools connection for proxying the worker state machine
    #devtools: ReducerDevtoolsConnection<TradingSocketDevtoolsState, TradingSocketWorkerEvent> | undefined;

    get isConnected(): ReadonlySignal<boolean> {
        return this.#isConnected as ReadonlySignal<boolean>;
    }

    get isAuthenticated(): ReadonlySignal<boolean> {
        return this.#isAuthenticated as ReadonlySignal<boolean>;
    }

    get joinedAccounts(): ReadonlySignal<Set<string>> {
        return this.#joinedAccounts as ReadonlySignal<Set<string>>;
    }

    get orderUpdates$(): Subject<OrderUpdateReport & OrderUpdate> {
        return this.#orderUpdates$;
    }

    get orderSnapshots$(): Subject<Order> {
        return this.#orderSnapshots$;
    }

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('TradingSocketProvider') private provider: TradingSocketProvider,
        @inject('SessionService')
        @optional()
        private sessionService?: ReactBindings['SessionService'],
    ) {
        this.#log = logger.getSubLogger({ name: 'TradingSocketService' }, { component: 'trading' });
        if (TRACE) {
            this.#log.trace('Initializing service');
        }

        this.#devtools = connectReducerDevtools({ name: 'TradingSocketWorker' });
        if (this.#devtools) {
            this.#disposableStack.use(this.#devtools);
            this.#devtools.init(this.#getDevtoolsState());
        }

        this.#initPromise = this.#initialize();

        // Send the current token
        // Send the token when it changes
        this.#disposableStack.defer(
            this.store
                .subscribeSelector(
                    (state) => state.auth.masqAccessToken?.token || state.auth.accessToken?.token || null,
                    { leading: true },
                )
                .handle((token) => this.#syncEffectiveAccessToken(token)),
        );

        if (this.sessionService) {
            this.#disposableStack.defer(
                effect(() => {
                    this.#syncEffectiveAccessToken(this.sessionService?.accessToken.value);
                }),
            );
        }
    }

    #syncEffectiveAccessToken(reduxEffectiveToken?: string | null): void {
        const token = this.sessionService
            ? (this.store.getState().auth.masqAccessToken?.token ?? this.sessionService.accessToken.value)
            : (reduxEffectiveToken ?? null);

        if (token === this.#effectiveAccessToken) {
            return;
        }
        this.#effectiveAccessToken = token;

        if (token === null) {
            this.#lastRejectedAccessToken = null;
            void this.clearAuthentication().catch((error) => {
                this.#log.error('Failed to clear trading socket authentication', { error });
            });
            return;
        }

        void this.#authenticateEffectiveToken(token);
    }

    async #authenticateEffectiveToken(token: string): Promise<void> {
        // The effective identity may have changed with the token (e.g. masquerade),
        // so refresh the worker-side client context before authenticating. Context
        // is observability-only: a failure here must never block authentication.
        try {
            await this.#sendClientContext();
        } catch (error) {
            this.#log.error('Failed to send client context after token change', { error });
        }

        if (token !== this.#effectiveAccessToken) {
            return;
        }

        try {
            await this.authenticate(token);
        } catch (error) {
            this.#log.error('Failed to authenticate after token change', { error });
            if (error instanceof TradingSocketTokenRejectedError) {
                this.#reportAuthenticationFailure(token);
            }
        }
    }

    #reportAuthenticationFailure(rejectedToken: string): void {
        if (rejectedToken === this.#lastRejectedAccessToken) {
            return;
        }

        this.#lastRejectedAccessToken = rejectedToken;
        void this.sessionService?.refresh(rejectedToken).catch((error) => {
            this.#log.error('Failed to report trading socket token rejection', { error });
        });
    }

    /**
     * Identify this client to the worker so its logs can distinguish tabs.
     * Fire-and-forget: the worker stores the context per port and never merges
     * it into shared state, so one tab cannot overwrite another's context.
     */
    async #sendClientContext(): Promise<void> {
        await this.#initPromise;
        if (this.#disposed || !this.#port) {
            return;
        }

        const auth = this.store.getState().auth;
        const context: TradingSocketClientContext = {
            clientId: this.#clientId,
            sessionId: getDatadogSessionId(),
            userId: auth.masqAccessToken?.token ? auth.masqUser?.id : auth.user?.id,
        };

        this.#port.postMessage(
            JSON.stringify({ type: 'client.context', id: v4(), payload: context } satisfies TradingSocketWorkerRequest),
        );
    }

    async #initialize() {
        try {
            this.#port = await this.provider.getWorkerPort();
            this.#port.start();
            this.#port.addEventListener('message', this.#handleWorkerMessage);

            this.#disposableStack.defer(() => {
                if (this.#port) {
                    this.#port.postMessage(
                        JSON.stringify({ type: 'client.dispose', id: v4() } satisfies TradingSocketWorkerRequest),
                    );
                    this.#port.removeEventListener('message', this.#handleWorkerMessage);
                    this.#port.close();
                }
            });
        } catch (error) {
            this.#log.error('Failed to initialize worker connection', { error });
        }
    }

    #getDevtoolsState(): TradingSocketDevtoolsState {
        return {
            workerState: this.#workerState.value,
            isConnected: this.#isConnected.value,
            isAuthenticated: this.#isAuthenticated.value,
            joinedAccountCount: this.#joinedAccounts.value.size,
        };
    }

    /**
     * Update connection flags based on worker state machine state
     */
    #updateConnectionFlags(state: TradingSocketWorkerState): void {
        switch (state) {
            case 'initial':
            case 'connecting':
                this.#isConnected.value = false;
                this.#isAuthenticated.value = false;
                break;
            case 'connected':
            case 'authenticating':
                this.#isConnected.value = true;
                this.#isAuthenticated.value = false;
                break;
            case 'authenticated':
            case 'reconciling':
                this.#isConnected.value = true;
                this.#isAuthenticated.value = true;
                break;
        }
    }

    /**
     * Handle messages from the worker
     */
    readonly #handleWorkerMessage = (event: MessageEvent) => {
        let message: TradingSocketWorkerMessage;

        try {
            // Use reviver to properly deserialize DateTime, BigDecimal, and other custom types
            message = JSON.parse(event.data, reviver);
            if (TRACE) {
                this.#log.trace('Received message from worker', { messageType: message.type });
            }
        } catch (error) {
            this.#log.error('Failed to parse worker message', { error, data: event.data });
            return;
        }

        // Handle events (no request ID)
        if (this.#isEvent(message)) {
            this.#handleEvent(message);
            return;
        }

        // Handle responses (with request ID)
        const response = message as TradingSocketWorkerResponse;
        const pendingRequest = this.#pendingRequests.get(response.id);

        if (!pendingRequest) {
            if (TRACE) {
                this.#log.trace('Received response for unknown request', { id: response.id, type: response.type });
            }
            return;
        }

        // Clear the timeout and remove the request
        clearTimeout(pendingRequest.timeoutId);
        this.#pendingRequests.delete(response.id);

        if ('error' in response && response.error) {
            const errorMessage = response.error.error.message || 'Request failed';
            this.#log.error('Worker returned error response', {
                error: new Error(errorMessage),
                requestType: response.type,
                requestId: response.id,
            });
            const requestId = response.error.requestId ?? response.id;
            pendingRequest.reject(
                response.type === 'auth' && response.failure === 'token-rejected'
                    ? new TradingSocketTokenRejectedError(errorMessage, requestId)
                    : new TradingSocketError(errorMessage, requestId),
            );
        } else {
            pendingRequest.resolve(response);
        }
    };

    /**
     * Check if a message is an event (no request ID)
     */
    #isEvent(message: TradingSocketWorkerMessage): message is TradingSocketWorkerEvent {
        return !('id' in message);
    }

    /**
     * Handle event messages from worker
     */
    #handleEvent(event: TradingSocketWorkerEvent) {
        switch (event.type) {
            case 'ready':
                // Ready event is handled by the one-time listener in #initializeWorker
                // This case is here to prevent "Unknown event type" errors
                break;

            case 'state-changed':
                if (TRACE) {
                    this.#log.trace('Worker state changed', { state: event.state });
                }
                this.#workerState.value = event.state;
                this.#updateConnectionFlags(event.state);
                break;

            case 'connected':
                if (TRACE) {
                    this.#log.trace('Socket connected');
                }
                this.#updateConnectionFlags('connected');
                break;

            case 'disconnected':
                if (TRACE) {
                    this.#log.trace('Socket disconnected');
                }
                this.#updateConnectionFlags('connecting');
                // Clear joined accounts on disconnect
                this.#joinedAccounts.value = new Set();
                // Clear all pending requests on disconnect
                this.#clearPendingRequests('Worker disconnected');
                break;

            case 'authentication-succeeded':
                if (TRACE) {
                    this.#log.trace('Trading socket authentication succeeded');
                }
                this.#updateConnectionFlags('authenticated');
                break;

            case 'authentication-rejected':
                this.#isAuthenticated.value = false;
                this.#joinedAccounts.value = new Set();
                break;

            case 'authentication-cleared':
                this.#isAuthenticated.value = false;
                this.#joinedAccounts.value = new Set();
                break;

            case 'account.joined':
                if (TRACE) {
                    this.#log.trace('Accounts joined', { accounts: event.payload });
                }
                // Update joined accounts set
                const newJoinedAccounts = new Set(this.#joinedAccounts.value);
                for (const accountId of event.payload.accounts) {
                    newJoinedAccounts.add(accountId);
                }
                this.#joinedAccounts.value = newJoinedAccounts;
                break;

            case 'account.left':
                if (TRACE) {
                    this.#log.trace('Accounts left', { accounts: event.payload });
                }
                // Update joined accounts set
                const updatedAccounts = new Set(this.#joinedAccounts.value);
                for (const accountId of event.payload.accounts) {
                    updatedAccounts.delete(accountId);
                }
                this.#joinedAccounts.value = updatedAccounts;
                break;

            case 'order.updated':
                if (TRACE) {
                    this.#log.trace('Received order update', { order: event.payload });
                }
                this.#orderUpdates$.next(event.payload);
                break;

            case 'order.snapshot':
                if (TRACE) {
                    this.#log.trace('Received order snapshot', { order: event.payload });
                }
                this.#orderSnapshots$.next(event.payload);
                break;

            default: {
                this.#log.error('Unknown event type', { type: (event as any).type });
                break;
            }
        }

        this.#devtools?.send(event, this.#getDevtoolsState());
    }

    /**
     * Clear all pending requests with an error message
     */
    #clearPendingRequests(reason: string): void {
        if (this.#pendingRequests.size > 0) {
            if (TRACE) {
                this.#log.trace('Clearing pending requests', { count: this.#pendingRequests.size, reason });
            }
            for (const [requestId, request] of this.#pendingRequests.entries()) {
                clearTimeout(request.timeoutId);
                request.reject(new TradingSocketError(reason, requestId));
            }
            this.#pendingRequests.clear();
        }
    }

    /**
     * Send a request to the worker and wait for response
     */
    async #sendRequest<T extends TradingSocketWorkerRequest['type']>(
        request: TradingSocketWorkerRequest & { type: T },
    ): Promise<Extract<TradingSocketWorkerResponse, { type: T }>> {
        await this.#initPromise;

        return new Promise((resolve, reject) => {
            // Fail fast after disposal: the port is closed, so a posted request would
            // never be answered and would surface as a misleading 30s timeout.
            if (this.#disposed) {
                reject(
                    new TradingSocketError(
                        `TradingSocketService disposed; dropped request: ${request.type}`,
                        request.id,
                    ),
                );
                return;
            }

            if (!this.#port) {
                reject(new TradingSocketError('Worker port not initialized', request.id));
                return;
            }

            // Set up a timeout to prevent memory leaks from requests that never receive a response
            const timeoutId = setTimeout(() => {
                this.#pendingRequests.delete(request.id);
                this.#log.error('Worker request timed out without response', {
                    type: request.type,
                    id: request.id,
                    timeoutMs: this.#REQUEST_TIMEOUT_MS,
                });
                reject(
                    new TradingSocketError(
                        `Worker request timed out after ${this.#REQUEST_TIMEOUT_MS}ms without response: ${request.type}`,
                        request.id,
                    ),
                );
            }, this.#REQUEST_TIMEOUT_MS);

            this.#pendingRequests.set(request.id, {
                resolve: resolve as any,
                reject,
                timeoutId,
            });

            if (TRACE) {
                this.#log.trace('Sending request to worker', { request });
            }

            const serialized = JSON.stringify(request);
            this.#port.postMessage(serialized);
        });
    }

    /**
     * Internal authenticate method that doesn't wait for initialization.
     * Used by the constructor's initialization chain.
     */
    async authenticate(token: string): Promise<void> {
        const existingAuthenticatePromise = this.#authenticatePromises.get(token);
        if (existingAuthenticatePromise) {
            return existingAuthenticatePromise;
        }

        if (TRACE) {
            this.#log.trace('Authenticating...');
        }

        const authenticatePromise = this.#sendRequest({ type: 'auth', id: v4(), token })
            .then(() => {
                if (TRACE) {
                    this.#log.trace('Authenticated');
                }
            })
            .catch((error) => {
                this.#log.error('Failed to authenticate', { error });
                throw error;
            })
            .finally(() => {
                this.#authenticatePromises.delete(token);
            });

        this.#authenticatePromises.set(token, authenticatePromise);

        return authenticatePromise;
    }

    async clearAuthentication(): Promise<void> {
        await this.#sendRequest({ type: 'auth.clear', id: v4() });
        this.#joinedAccounts.value = new Set();
    }

    async disconnect(): Promise<void> {
        await this.#sendRequest({ type: 'disconnect', id: v4() });
    }

    /**
     * Subscribe to accounts.
     * Returns a dispose function to clean up the subscription.
     */
    subscribeToAccounts(accountIds: string[]): () => void {
        if (TRACE) {
            this.#log.trace('Subscribe to accounts', { accountIds });
        }

        // Update current accounts
        this.#currentAccounts = new Set(accountIds);

        // Reconcile accounts with the worker
        this.#triggerReconciliation();

        // Return a dispose function
        return () => {
            if (TRACE) {
                this.#log.trace('Unsubscribe from accounts');
            }
            this.#currentAccounts.clear();
            this.#triggerReconciliation();
        };
    }

    #triggerReconciliation() {
        this.#reconcileAccounts();
    }

    async #reconcileAccounts(): Promise<void> {
        // A widget tearing down can trigger one last reconciliation after the service
        // (and its worker port) was disposed; the worker already removes this port's
        // accounts on dispose, so there is nothing left to reconcile.
        if (this.#disposed) {
            if (TRACE) {
                this.#log.trace('Skipping account reconciliation after dispose');
            }
            return;
        }

        // If already reconciling, queue another run
        if (this.#reconcilingAccounts) {
            this.#reconcileQueued = true;
            return;
        }

        this.#reconcilingAccounts = true;

        try {
            do {
                this.#reconcileQueued = false;

                // Use current accounts as target
                const currentTarget = new Set(this.#currentAccounts);

                if (TRACE) {
                    this.#log.trace('Reconciling accounts', { accounts: Array.from(currentTarget) });
                }

                await this.#sendRequest({
                    type: 'account.reconcile',
                    id: v4(),
                    payload: { accounts: Array.from(currentTarget) },
                });

                // Update local state to match what we just reconciled
                this.#joinedAccounts.value = currentTarget;
            } while (this.#reconcileQueued);
        } catch (error) {
            if (this.#disposed) {
                if (TRACE) {
                    this.#log.trace('Ignoring reconciliation failure after dispose', { error });
                }
                return;
            }
            this.#log.error('Error reconciling accounts', { error });
        } finally {
            this.#reconcilingAccounts = false;
        }
    }

    async createOrder(data: SendOrderData): Promise<Order> {
        const requestId = v4();

        this.#log.info('Sending order create request to trading socket', {
            requestId,
            account: data.account,
            instrument: data.instrument,
            side: data.side,
            quantity: data.quantity,
        });

        if (TRACE) {
            this.#log.trace('Order create request details', { requestId, data });
        }

        // TODO: Remove cast when @thinkalpha/trading-ws-client exports z.input types for worker messages.
        // Currently the worker types use z.infer (output types with LocalDate), but we send z.input
        // (input types with string dates) since JSON serialization converts everything to strings anyway.
        const response = await this.#sendRequest({
            type: 'order.create',
            id: requestId,
            payload: data as NewOrderBase,
        });

        // If we reach here, response.success must be true (errors are thrown by #sendRequest)
        const createdOrder = (response as Extract<typeof response, { success: true }>).payload;

        this.#log.info('Order created successfully', {
            requestId,
            orderId: createdOrder.id,
        });

        return createdOrder;
    }

    async createBatchOrders(data: BatchOrderInput): Promise<Order[]> {
        const requestId = v4();
        const orderCount = data.orders.length;

        this.#log.info('Sending batch order request to trading socket', {
            requestId,
            orderCount,
            accounts: [...new Set(data.orders.map((o) => o.account))],
        });

        if (TRACE) {
            this.#log.trace('Batch order request details', { requestId, orders: data.orders });
        }

        // TODO: Remove cast when @thinkalpha/trading-ws-client exports z.input types for worker messages.
        // Currently the worker types use z.infer (output types with LocalDate), but we send z.input
        // (input types with string dates) since JSON serialization converts everything to strings anyway.
        const response = await this.#sendRequest({
            type: 'order.batch-create',
            id: requestId,
            payload: data as NewOrderBatch,
        });

        if (response.type !== 'order.batch-create') {
            this.#log.error('Unexpected response type for batch order creation', {
                requestId,
                expectedType: 'order.batch-create',
                actualType: response.type,
            });
            throw new TradingSocketError('Unexpected response type', response.id);
        }

        // If we reach here, response.success must be true (errors are thrown by #sendRequest)
        const successResponse = response as Extract<typeof response, { success: true }>;

        if (!successResponse.payload.orders) {
            this.#log.error('Batch order creation returned no orders', {
                requestId,
                error: new Error('Batch order creation failed'),
            });
            throw new TradingSocketError('Batch order creation failed', response.id);
        }

        const createdOrders = successResponse.payload.orders;

        this.#log.info('Batch order creation successful', {
            requestId,
            submittedCount: orderCount,
            createdCount: createdOrders.length,
            orderIds: createdOrders.map((o) => o.id),
        });

        return createdOrders;
    }

    dispose(): void {
        this.#disposed = true;
        // Cancel ongoing operations if any
        this.#authenticatePromises.clear();
        // Clear all pending requests before disposing
        this.#clearPendingRequests('Service disposed');
        this.#disposableStack.dispose();
        this.#orderUpdates$.complete();
        this.#orderSnapshots$.complete();
    }

    [Symbol.dispose](): void {
        this.dispose();
    }
}
